// A scene's terrain, twin of MapLibrary.normalize_terrain and
// MapScene#terrain_is_valid: { rows: [...] }, one string per row of cells and
// one tile code per cell, EMPTY for none. Empty cells at the end of a row and
// empty rows at the end are trimmed, so one painting has one spelling. A cut
// cell's corner is a second list beside `rows`: cuts.ts.
export const EMPTY = ".";
export const MAX_CELLS = 200;
// Samples per shorter cell side along a stroke: enough to cross a corner.
const STROKE_SAMPLES = 6;

export function terrainRows(terrain) {
	return Array.isArray(terrain?.rows) ? [...terrain.rows] : [];
}

// Whether two row lists paint the same cells, whatever their identity.
export function sameRows(a, b) {
	return a.length === b.length && a.every((line, row) => line === b[row]);
}

export function codeAt(rows, col, row) {
	const code = rows[row]?.[col];
	return code && code !== EMPTY ? code : null;
}

export function paintCells(rows, cells, code) {
	const next = [...rows];
	for (const { col, row } of cells) {
		if (!inRange(col) || !inRange(row)) continue;
		while (next.length <= row) next.push("");
		const line = next[row];
		if (code === EMPTY && col >= line.length) continue;
		const padded = line.padEnd(col + 1, EMPTY);
		next[row] = padded.slice(0, col) + code + padded.slice(col + 1);
	}
	return normalize(next);
}

// Only cells whose centre lies on the scene and that storage holds, so a
// stroke past its edge paints nothing there (twin of MapFog#paintable?).
export function paintable(math, scene, { col, row }) {
	if (!inRange(col) || !inRange(row)) return false;
	const { x, y } = math.centerOf(col, row);
	return x >= 0 && y >= 0 && x <= scene.width_px && y <= scene.height_px;
}

// The cells a stroke from one image point to another crosses, in order, each with
// the points of the stroke that fell in it (`x`, `y` the first of them).
export function strokeCells(math, from, to) {
	const step = Math.min(math.grid.cell_w, math.grid.cell_h) / STROKE_SAMPLES;
	const count = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / step));
	const cells = new Map();
	for (let index = 0; index <= count; index++) {
		const t = index / count;
		const point = { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
		const cell = math.cellAt(point.x, point.y);
		const key = `${cell.col},${cell.row}`;
		if (cells.has(key)) cells.get(key).points.push(point);
		else cells.set(key, { ...cell, ...point, points: [point] });
	}
	return [...cells.values()];
}

function inRange(index) {
	return Number.isInteger(index) && index >= 0 && index < MAX_CELLS;
}

function normalize(rows) {
	const trimmed = rows.map((line) => line.replace(/\.+$/, ""));
	while (trimmed.length && trimmed.at(-1) === "") trimmed.pop();
	return trimmed;
}
